import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../Config/api';
import useTurnstile from '../Hooks/useTurnstile';

export default function ActivatePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(''); // '' | sending | sent | error
  const { enabled: captchaEnabled, token: captchaToken, resetTurnstile, TurnstileWidget } = useTurnstile();

  const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const defaultLogoUrl = `${apiBaseUrl}/api/auth/media/branding/a-sight_LOGO_DEF.png`;
  const [brandingLogoUrl, setBrandingLogoUrl] = useState(defaultLogoUrl);

  const calledOnceRef = useRef(false); // blocco doppio effetto

  useEffect(() => {
    const url = ref
        ? `${API_ENDPOINTS.brandingLogo}?ref=${encodeURIComponent(ref)}`
        : API_ENDPOINTS.brandingLogo;
    fetch(url)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
            if (data?.logo_url) setBrandingLogoUrl(data.logo_url);
        })
        .catch(() => {});
  }, [ref]);


  const activateAccount = async () => {
    setStatus('loading');
    try {
      const res = await fetch(API_ENDPOINTS.activateAccountByToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch (e) {
      setStatus('error');
    }
  };

  useEffect(() => {
    if (!calledOnceRef.current) {
      calledOnceRef.current = true;
      activateAccount();
    }
  }, []);



  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white p-8 rounded shadow-md w-full max-w-md text-center space-y-6">
        <img
          src={brandingLogoUrl}
          alt="Logo"
          draggable="false"
          style={{ maxWidth: 120, margin: '0 auto', display: 'block', userSelect: 'none' }}
          className="mb-2"
        />
        {status === 'loading' && (
          <>
            <p className="text-gray-700 text-lg">Attivazione in corso...</p>
            <div className="animate-spin h-6 w-6 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          </>
        )}

        {status === 'success' && (
          <>
            <h2 className="text-xl font-bold text-green-600">Account attivato!</h2>
            <p className="text-gray-600">Ora puoi impostare la tua password.</p>
            <button
              onClick={() => navigate(`/reset-password/${token}${ref ? `?ref=${ref}` : ''}`)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
            >
              Continua
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <h2 className="text-xl font-bold text-red-600">Errore di attivazione</h2>
            <p className="text-gray-600">Il link potrebbe essere scaduto o già usato.</p>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-3 text-left">
              <p className="text-sm font-semibold text-yellow-800">Richiedi un nuovo link di attivazione:</p>
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="La tua email di registrazione"
                className="w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring focus:ring-yellow-300"
              />
              <button
                onClick={async () => {
                  if (!resendEmail) return;
                  setResendStatus('sending');
                  try {
                    const res = await fetch(API_ENDPOINTS.resendActivation, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ email: resendEmail, captcha_token: captchaToken }),
                    });
                    if (res.ok) {
                      setResendStatus('sent');
                    } else {
                      setResendStatus('error');
                    }
                  } catch {
                    setResendStatus('error');
                  }
                }}
                disabled={resendStatus === 'sending' || resendStatus === 'sent' || (captchaEnabled && !captchaToken)}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded text-sm font-semibold disabled:opacity-50"
              >
                {resendStatus === 'sending' ? 'Invio in corso...' : resendStatus === 'sent' ? 'Email inviata!' : 'Invia nuovo link'}
              </button>
              {TurnstileWidget}
              {resendStatus === 'sent' && (
                <p className="text-sm text-green-600">Controlla la tua casella email per il nuovo link di attivazione.</p>
              )}
              {resendStatus === 'error' && (
                <p className="text-sm text-red-600">Errore nell'invio. Riprova.</p>
              )}
            </div>

            <button
              onClick={activateAccount}
              className="mt-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded"
            >
              Riprova attivazione
            </button>
          </>
        )}
      </div>
    </div>
  );
}
